import React from "react";
import { AnimatePresence } from "framer-motion";
import { Share2 } from "lucide-react";
import {
  GoalCard, GoalStrip, ProfileCard, AssessmentCard, GapCard, ProcessingCard,
  HealthBar, PathwayRoadmap, ProgramMatchCard, ApplicationCard,
  MatchingCard, CompetencyDetail, WorkspaceIdle,
} from "@/components/mentis/WorkspaceCards";

const ASSESS_IDS = ["cosmetic-manufacturing", "cosmetic-formulation", "quality-gmp", "cosmetic-regulatory"];

export default function Workspace({ session, processing, matching, selected, setSelected }) {
  const phase = session.phase;
  const caps = session.capabilities || [];
  const [copied, setCopied] = React.useState(false);

  const share = async () => {
    const url = `${window.location.origin}/p/${session.id}`;
    try { await navigator.clipboard.writeText(url); } catch (e) { /* noop */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const showShare = ["pathway", "modify", "match", "application", "submitted"].includes(phase);

  const content = () => {
    if (phase === "landing") return <WorkspaceIdle />;

    if (processing) {
      return (
        <div className="space-y-5">
          {session.goal?.identified && <GoalCard goal={session.goal} learner={session.learner} />}
          <ProcessingCard steps={session.ui?.processing || []} />
        </div>
      );
    }
    if (matching) {
      return (
        <div className="space-y-5">
          <MatchingCard steps={session.ui?.steps || ["Competency requirements", "Prerequisites", "Academic requirements", "Program rules"]} />
        </div>
      );
    }

    if (phase === "background" || phase === "profile" || phase === "profile_confirmed") {
      return (
        <div className="space-y-5">
          <GoalCard goal={session.goal} learner={session.learner} />
          <ProfileCard capabilities={caps} building={phase === "background"} />
        </div>
      );
    }

    if (phase === "assessment") {
      const evidenceCaps = caps.filter((c) => ASSESS_IDS.includes(c.id));
      return (
        <div className="space-y-5">
          <GoalCard goal={session.goal} learner={session.learner} />
          <AssessmentCard assessment={session.assessment} evidenceCaps={evidenceCaps} />
          <ProfileCard capabilities={caps} />
        </div>
      );
    }

    if (phase === "gap") {
      return (
        <div className="space-y-5">
          <GoalCard goal={session.goal} learner={session.learner} />
          {session.gap && <GapCard gap={session.gap} />}
        </div>
      );
    }

    if (phase === "pathway" || phase === "modify") {
      return (
        <div className="space-y-5">
          <GoalStrip goal={session.goal} learner={session.learner} />
          {session.pathway && <HealthBar health={session.pathway.health} />}
          {session.pathway && <PathwayRoadmap pathway={session.pathway} onSelect={setSelected} />}
        </div>
      );
    }

    if (phase === "match") {
      return (
        <div className="space-y-5">
          <GoalStrip goal={session.goal} learner={session.learner} />
          {session.program_match && <ProgramMatchCard match={session.program_match} />}
          {session.pathway && <HealthBar health={session.pathway.health} />}
          {session.pathway && <PathwayRoadmap pathway={session.pathway} onSelect={setSelected} />}
        </div>
      );
    }

    if (phase === "application" || phase === "submitted") {
      return (
        <div className="space-y-5">
          {session.application && <ApplicationCard application={session.application} />}
          {session.program_match && <ProgramMatchCard match={session.program_match} />}
        </div>
      );
    }

    return <WorkspaceIdle />;
  };

  return (
    <div className="h-full overflow-y-auto scroll-thin p-4 sm:p-6 lg:p-8" data-testid="workspace-panel">
      <div className="max-w-3xl mx-auto">
        {showShare && (
          <div className="flex justify-end mb-4">
            <button data-testid="share-pathway-btn" onClick={share}
              className="flex items-center gap-2 text-xs font-medium border border-black/10 rounded-full px-3.5 py-1.5 text-slate-700 hover:text-orange-400 hover:border-orange-500/40 transition-colors">
              <Share2 size={13} /> {copied ? "Link copied" : "Share pathway"}
            </button>
          </div>
        )}
        <AnimatePresence mode="wait">{content()}</AnimatePresence>
      </div>
      <CompetencyDetail comp={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
